import React, {FC, useEffect, useMemo, useState} from 'react'
import {toast, ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import './MonthEndReports.css'
import {KTSVG} from '../../../../_metronic/helpers'
import {useAuth} from '../../auth'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import {TableSkeleton} from '../../../shared/components/LoadingSkeletons'
import {useGlobalFilter} from '../../../shared/context/GlobalFilterContext'
import {
  fetchAccessibleVesselsForCurrentUser,
  normalizeAccessibleVessels,
} from '../../../shared/access/accessibleVessels'
import {useMonthEndFeatureAccess} from '../core/monthEndAccess'
import QhseAccessDenied from './QhseAccessDenied'
import AccountsMonthEndReportModal, {
  type AccountsReportModalMode,
} from './AccountsMonthEndReportModal'
import {
  ACCOUNTS_MONTH_END_API_URL,
  type AccountsMonthEndReport,
  formatAmount,
  formatReportMonth,
  sumLines,
} from '../core/accountsMonthEnd'

const AccountsMonthEndReportPage: FC = () => {
  const {auth, currentUser} = useAuth()
  const {state: globalState, vessels: globalVessels} = useGlobalFilter()
  const {canRead, canCreate, canUpdate, canDelete, hasAssignedVessel, assignedVesselId} =
    useMonthEndFeatureAccess('module1-report')

  const [reports, setReports] = useState<AccountsMonthEndReport[]>([])
  const [vessels, setVessels] = useState<Array<{id: string; name: string}>>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterVesselId, setFilterVesselId] = useState(
    hasAssignedVessel && assignedVesselId ? String(assignedVesselId) : ''
  )
  const [filterMonth, setFilterMonth] = useState('')
  const [refreshKey, setRefreshKey] = useState(0)

  const [modalMode, setModalMode] = useState<AccountsReportModalMode | null>(null)
  const [activeReport, setActiveReport] = useState<AccountsMonthEndReport | null>(null)

  // Sync global vessel filter
  useEffect(() => {
    if (!hasAssignedVessel) {
      if (globalState.vessel?.id) {
        setFilterVesselId(String(globalState.vessel.id))
      } else {
        setFilterVesselId('')
      }
    }
  }, [globalState.vessel, hasAssignedVessel])

  /* ── Vessel list drives the filter dropdown and resolves names for display ── */
  useEffect(() => {
    if (hasAssignedVessel && currentUser?.vessel) {
      const vessel = currentUser.vessel as any
      setVessels([
        {
          id: String(vessel.id),
          name: vessel.fleet_name || vessel.name || `Vessel #${vessel.id}`,
        },
      ])
      return
    }

    let cancelled = false
    const loadVessels = async () => {
      try {
        const list = normalizeAccessibleVessels(await fetchAccessibleVesselsForCurrentUser())
        if (cancelled) return
        setVessels(
          list.map((vessel: any) => ({
            id: String(vessel.id),
            name: vessel.fleet_name || vessel.name || `Vessel #${vessel.id}`,
          }))
        )
      } catch (error) {
        if (!cancelled) setVessels([])
      }
    }
    loadVessels()
    return () => {
      cancelled = true
    }
  }, [hasAssignedVessel, currentUser])

  const visibleVessels = useMemo(() => {
    if (globalState.company?.id && globalVessels?.length) {
      const companyVesselIds = new Set(
        globalVessels
          .filter((v: any) => String(v.companyId || v.company_id || v.company?.id) === String(globalState.company?.id))
          .map((v: any) => String(v.id))
      )
      if (companyVesselIds.size > 0) {
        return vessels.filter((v) => companyVesselIds.has(v.id))
      }
    }
    return vessels
  }, [vessels, globalState.company, globalVessels])

  const loadReports = async () => {
    if (!auth?.auth?.jwt) return
    setIsLoading(true)
    try {
      const response = await fetch(ACCOUNTS_MONTH_END_API_URL, {
        headers: {Authorization: `Bearer ${auth.auth.jwt}`},
      })
      if (!response.ok) throw new Error('Failed to load reports')
      const payload = await response.json()
      const rows = Array.isArray(payload) ? payload : payload?.content || []
      setReports(rows)
      setLoadFailed(false)
    } catch (error) {
      setReports([])
      setLoadFailed(true)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadReports()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.auth?.jwt, refreshKey])

  const vesselNameById = useMemo(() => {
    const map = new Map<string, string>()
    vessels.forEach((vessel) => map.set(vessel.id, vessel.name))
    return map
  }, [vessels])

  const resolveVesselName = (report: AccountsMonthEndReport) =>
    report.vesselName || vesselNameById.get(String(report.vesselId ?? '')) || '-'

  const filteredReports = useMemo(() => {
    const search = searchQuery.trim().toLowerCase()
    const [filterYear, filterMonthNo] = filterMonth ? filterMonth.split('-') : ['', '']

    return reports.filter((report) => {
      const companyMatches =
        !globalState.company?.id ||
        !visibleVessels.length ||
        visibleVessels.some((v) => String(v.id) === String(report.vesselId ?? ''))
      const vesselMatches =
        !filterVesselId || String(report.vesselId ?? '') === String(filterVesselId)
      const monthMatches =
        !filterMonth ||
        (String(report.reportYear ?? '') === filterYear &&
          Number(report.reportMonth ?? 0) === Number(filterMonthNo))
      const searchMatches =
        !search ||
        resolveVesselName(report).toLowerCase().includes(search) ||
        (report.remarks || '').toLowerCase().includes(search) ||
        (report.createdByName || '').toLowerCase().includes(search)

      return companyMatches && vesselMatches && monthMatches && searchMatches
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports, searchQuery, filterVesselId, filterMonth, vesselNameById, globalState.company, visibleVessels])

  const handleDelete = async (report: AccountsMonthEndReport) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return
    try {
      const response = await fetch(`${ACCOUNTS_MONTH_END_API_URL}/${report.id}`, {
        method: 'DELETE',
        headers: {Authorization: `Bearer ${auth?.auth?.jwt}`},
      })
      if (!response.ok) throw new Error('Failed to delete report')
      toast.success('Report deleted')
      setRefreshKey((key) => key + 1)
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete report')
    }
  }

  const openModal = (mode: AccountsReportModalMode, report: AccountsMonthEndReport | null) => {
    setActiveReport(report)
    setModalMode(mode)
  }

  const closeModal = () => {
    setModalMode(null)
    setActiveReport(null)
  }

  const formatDate = (value?: string | null) => {
    if (!value) return '-'
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return '-'
    return parsed.toLocaleDateString('en-US', {year: 'numeric', month: 'short', day: 'numeric'})
  }

  if (!canRead) {
    return (
      <QhseAccessDenied message='Access denied — you do not have permission to view Month End Reports.' />
    )
  }

  return (
    <div className='month-end-reports-wrapper'>
      <div className='card' style={{boxShadow: 'none'}}>
        <div className='card-header border-0 pt-5 d-flex flex-column gap-3'>
          <div className='d-flex align-items-start justify-content-between flex-wrap gap-3'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label fw-bold fs-3 mb-1'>Accounts Month End Report</span>
              <span className='text-muted mt-1 fw-semibold fs-7'>
                Monthly stock, consumption and cost statements per vessel
              </span>
            </h3>

            <button
              type='button'
              className='btn btn-primary'
              onClick={() => openModal('add', null)}
              disabled={!canCreate}
            >
              <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2 me-2' />
              Add Report
            </button>
          </div>

          <div className='d-flex align-items-end gap-3 flex-wrap'>
            <div style={{minWidth: 220}}>
              <label className='form-label fw-semibold fs-7 text-muted mb-1'>Vessel</label>
              {hasAssignedVessel ? (
                <div
                  className='form-control form-control-solid bg-light text-muted'
                  style={{cursor: 'not-allowed'}}
                >
                  {vessels[0]?.name || 'My Vessel'}
                </div>
              ) : (
                <SearchableSelect
                  className='form-select form-select-solid'
                  value={filterVesselId}
                  onChange={(e) => setFilterVesselId(e.target.value)}
                >
                  <option value=''>All Vessels</option>
                  {visibleVessels.map((vessel) => (
                    <option key={vessel.id} value={vessel.id}>
                      {vessel.name}
                    </option>
                  ))}
                </SearchableSelect>
              )}
            </div>

            <div style={{minWidth: 180}}>
              <label className='form-label fw-semibold fs-7 text-muted mb-1'>Month</label>
              <input
                type='month'
                className='form-control form-control-solid'
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
              />
            </div>

            <div className='flex-grow-1' style={{minWidth: 220}}>
              <label className='form-label fw-semibold fs-7 text-muted mb-1'>Search</label>
              <input
                type='text'
                className='form-control form-control-solid'
                placeholder='Search vessel, remarks or created by...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className='card-body pt-3'>
          <div className='table-responsive'>
            <table className='table table-bordered align-middle mb-0'>
              <thead className='table-header'>
                <tr className='fw-bold text-muted'>
                  <th className='text-center' style={{width: 70}}>
                    SR/NO
                  </th>
                  <th style={{minWidth: 180}}>Vessel</th>
                  <th style={{minWidth: 120}}>Month</th>
                  <th className='text-center' style={{minWidth: 90}}>
                    Items
                  </th>
                  <th className='text-end' style={{minWidth: 130}}>
                    Total Consumption
                  </th>
                  <th className='text-end' style={{minWidth: 110}}>
                    Total Bunker
                  </th>
                  <th className='text-end' style={{minWidth: 110}}>
                    Total ROB
                  </th>
                  <th style={{minWidth: 150}}>Created By</th>
                  <th style={{minWidth: 130}}>Created Date</th>
                  <th className='text-center' style={{minWidth: 150}}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <TableSkeleton rows={8} cols={10} />
                ) : filteredReports.length === 0 ? (
                  <tr>
                    <td colSpan={10} className='text-center text-muted py-12'>
                      {loadFailed
                        ? 'Unable to load Accounts Month End Reports.'
                        : 'No Accounts Month End Reports found.'}
                    </td>
                  </tr>
                ) : (
                  filteredReports.map((report, index) => (
                    <tr key={report.id}>
                      <td className='text-center text-muted'>{index + 1}</td>
                      <td className='text-dark fw-semibold'>{resolveVesselName(report)}</td>
                      <td>{formatReportMonth(report.reportYear, report.reportMonth)}</td>
                      <td className='text-center'>
                        <span className='badge badge-light-info'>{report.lines?.length ?? 0}</span>
                      </td>
                      <td className='text-end'>
                        {formatAmount(report.totalConsumption != null ? report.totalConsumption : sumLines(report.lines, 'consumption'))}
                      </td>
                      <td className='text-end'>
                        {formatAmount(report.totalBunker != null ? report.totalBunker : sumLines(report.lines, 'bunker'))}
                      </td>
                      <td className='text-end'>
                        {formatAmount(report.totalRob != null ? report.totalRob : sumLines(report.lines, 'rob'))}
                      </td>
                      <td className='text-muted'>{report.createdByName || '-'}</td>
                      <td className='text-muted'>{formatDate(report.createdDate)}</td>
                      <td className='text-center'>
                        <div className='d-flex align-items-center justify-content-center gap-1'>
                          <button
                            type='button'
                            className='btn btn-icon btn-sm'
                            title='View'
                            onClick={() => openModal('view', report)}
                          >
                            <KTSVG
                              path='/media/map/ph_eye.svg'
                              className='svg-icon-3 text-primary'
                            />
                          </button>

                          {canUpdate && (
                            <button
                              type='button'
                              className='btn btn-icon btn-sm'
                              title='Edit'
                              onClick={() => openModal('edit', report)}
                            >
                              <KTSVG path='/media/map/edit-active.svg' className='svg-icon-3' />
                            </button>
                          )}

                          {canDelete && (
                            <button
                              type='button'
                              className='btn btn-icon btn-sm'
                              title='Delete'
                              onClick={() => handleDelete(report)}
                            >
                              <KTSVG
                                path='/media/map/red-cross-icon.svg'
                                className='svg-icon-3'
                              />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <AccountsMonthEndReportModal
        isOpen={modalMode !== null}
        mode={modalMode ?? 'add'}
        report={activeReport}
        onClose={closeModal}
        onSaved={() => setRefreshKey((key) => key + 1)}
      />

      <ToastContainer position='top-right' autoClose={3000} />
    </div>
  )
}

export default AccountsMonthEndReportPage
